/**
 * showDeleteUndoToast — Sonner toast with an inline Undo action.
 *
 * Contract:
 *   showDeleteUndoToast({ unitId, productName, onUndo })
 *
 *   * Rendered as a persistent (30s) toast so admins have a real window
 *     to notice + click. After 30s the toast auto-dismisses; the delete
 *     stays permanent from the admin's POV (the backend keeps a
 *     `stock_units_trash` row for a longer audit trail, but the restore
 *     endpoint will 410 anything past the window).
 *   * "Undo" fires the `onUndo` callback, which POSTs
 *     /api/stock/units/{unit_id}/restore. On success it calls the caller-
 *     provided refresh so the row reappears in the table. On failure
 *     (409/410 already-expired) it toasts the server message —
 *     no silent failures.
 *
 * Why extracted:
 *   Sonner's action-toast API is verbose, and both the Purchases-tab
 *   inline delete and the Edit-modal delete need the same UX. One
 *   helper keeps both paths pixel-identical.
 */
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

// Matches backend UNDO_WINDOW_SECONDS in routes/stock.py — DO NOT let
// these drift. Client dismisses the toast slightly before the server
// window closes so a click at t=29.9s is still restore-able.
const UNDO_WINDOW_MS = 30_000;
const TOAST_DURATION_MS = 28_000;

export const showDeleteUndoToast = ({ unitId, productName, onRestored }) => {
  const label = productName || "delivery line";
  const startedAt = Date.now();

  toast(`Deleted: ${label}`, {
    description: "Undo within 30 seconds to restore this line exactly as it was.",
    duration: TOAST_DURATION_MS,
    action: {
      label: "Undo",
      onClick: async () => {
        // Client-side window guard — cheap sanity check before the
        // network call. Server has its own 410 guard, we just avoid a
        // pointless round-trip if the user found the button 45s late.
        if (Date.now() - startedAt > UNDO_WINDOW_MS) {
          toast.error("Undo window has closed");
          return;
        }
        try {
          await axios.post(`${API}/stock/units/${unitId}/restore`);
          toast.success(`Restored: ${label}`);
          onRestored?.();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Failed to restore");
        }
      },
    },
  });
};
